import * as protocol from "../../../apis/observationalProtocol";
import * as survey from "../../../apis/survey";
import { createSelector } from "reselect";
import { hideModal, fetchCaseProtocol } from "../../actions";
import { Node, LinkList, ListContainer } from "../../../utilities/linkedList";
import history from "../../../utilities/history";
import { uuidv4 } from "../../../utilities/utilities";
import { startSaving, stopSaving } from "../../actions";

function getBilateralRevisions(revisionsList) {
  let bilateralRevisions = revisionsList.length > 0 ? revisionsList[0] : [];
  if (revisionsList.length > 1 && revisionsList[0] && revisionsList[1]) {
    bilateralRevisions = revisionsList[0]?.map((revision, index) => {
      var differences = revision?.differences?.map((diff) => ({
        ...diff,
        area1OldValue: diff.oldValue,
        area1NewValue: diff.newValue,
        area2OldValue: revisionsList[1][index]?.differences?.find(
          (d) =>
            d.id === diff.id &&
            d.uniqueId === diff.uniqueId &&
            d.questionType === diff.questionType,
        )?.oldValue,
        area2NewValue: revisionsList[1][index]?.differences?.find(
          (d) =>
            d.id === diff.id &&
            d.uniqueId === diff.uniqueId &&
            d.questionType === diff.questionType,
        )?.newValue,
      }));

      var missingDifferences = revisionsList[1][index]?.differences?.filter(
        (diff) =>
          !differences.find(
            (d) =>
              d.id === diff.id &&
              d.uniqueId === diff.uniqueId &&
              d.questionType === diff.questionType,
          ),
      );
      if (missingDifferences?.length > 0) {
        missingDifferences = missingDifferences.map((diff) => ({
          ...diff,
          area1OldValue: null,
          area1NewValue: null,
          area2OldValue: diff.oldValue,
          area2NewValue: diff.newValue,
        }));

        differences = differences.concat(missingDifferences);
      }

      return {
        ...revision,
        differences: differences.sort((a, b) =>
          a.uniqueId > b.uniqueId ? 1 : -1,
        ),
      };
    });
  }
  return bilateralRevisions;
}

const SUBMIT_SURVEY_ANSWER_REQUESTED = "SUBMIT_SURVEY_ANSWER_REQUESTED";
const SUBMIT_SURVEY_ANSWER_SUCCESS = "SUBMIT_SURVEY_ANSWER_SUCCESS";
const SUBMIT_SURVEY_ANSWER_FAILED = "SUBMIT_SURVEY_ANSWER_FAILED";

export const submitSurvey =
  (
    caseIdOrTaskId,
    protocolId,
    instanceId,
    surveyId,
    patientId,
    delegateId,
    questionAnswers,
    requestType,
    status,
  ) =>
  async (dispatch, getState) => {
    let state = getState().protocol;
    let patientCaseSurveyInstances = state.patientCaseSurveyInstances;

    const isReOpened = parseInt(status) === 2;

    // For reopened surveys, capture edit reason before proceeding
    if (isReOpened) {
      return dispatch({
        type: "SHOW_EDIT_REASON_MODAL",
        data: {
          pendingSubmit: {
            caseIdOrTaskId,
            protocolId,
            instanceId,
            surveyId,
            patientId,
            delegateId,
            questionAnswers,
            requestType,
            status,
          },
        },
      });
    }

    // Apply prefill fallbacks before building sides and detecting corrections
    const augmentedAnswers = applyRepeatablePrefillFallbacks(
      questionAnswers,
      state.surveyQuestionHash,
      isReOpened,
    );

    // Build question answer sides
    let questionAnswerSides = [];
    Object.keys(patientCaseSurveyInstances).map((key) => {
      const side = {
        questionAnswers: augmentedAnswers[parseInt(key)],
        patientCaseSurveyInstanceId:
          patientCaseSurveyInstances[key].patientCaseSurveyInstanceId,
        bilateralAreaId: patientCaseSurveyInstances[key].bilateralAreaId,
        patientCaseSurveyInstanceVersion:
          patientCaseSurveyInstances[key].patientCaseSurveyInstanceVersion,
      };
      questionAnswerSides.push(side);
    });

    // Detect repeatable question changes
    const detectedCorrections = detectRepeatableQuestionChanges(
      state.surveyQuestionHash,
      questionAnswers,
      patientCaseSurveyInstances,
      isReOpened,
    );

    // If changes detected, check if propagation is needed
    if (detectedCorrections.length > 0) {
      try {
        const correctionRequests = detectedCorrections.map((c) => ({
          correctionSourceInstanceId: instanceId,
          bilateralAreaId: c.bilateralAreaId,
          questionBaseId: c.questionBaseId,
          questionTitle: c.questionTitle,
          questionType: c.questionType,
          originalValue: c.originalValue,
          correctedValue: c.correctedValue,
        }));

        // Choose the appropriate preview endpoint and check if there are other completed surveys to update
        const preview =
          requestType === "task"
            ? await survey.previewPropagationForTask(
                caseIdOrTaskId,
                correctionRequests,
              )
            : await survey.previewPropagation(
                caseIdOrTaskId,
                correctionRequests,
              );

        if (preview.requiresPropagation) {
          // First-time completion triggering propagation: capture edit reason for the source survey first
          return dispatch({
            type: "SHOW_EDIT_REASON_MODAL",
            data: {
              pendingSubmit: {
                caseIdOrTaskId,
                protocolId,
                instanceId,
                surveyId,
                patientId,
                delegateId,
                questionAnswers,
                requestType,
                status,
                pendingPropagation: {
                  correctionRequests,
                  preview,
                  questionAnswerSides,
                },
              },
            },
          });
        }
      } catch (error) {
        console.error("Failed to preview propagation", error);
        console.error(
          "   Error details:",
          error.response?.data || error.message,
        );

        // Log propagation preview errors
        fetch(`/api/utility/log`, {
          method: "post",
          headers: {
            "Content-Type": "application/json",
            "utility-log-location": window.location.href,
          },
          body: JSON.stringify({
            message:
              "Propagation Preview Error\n" +
              "Survey Url: " +
              window.location.href +
              "\n" +
              "Request Type: " +
              (requestType || "case") +
              "\n" +
              "Error: " +
              (error.message || JSON.stringify(error)) +
              "\n",
            priority: 3,
          }),
        }).catch((err) => console.error("Failed to log error", err));
      }
    }

    // Normal submit (no propagation needed or propagation already handled)
    dispatch({
      type: SUBMIT_SURVEY_ANSWER_REQUESTED,
      data: { isCreatingSurvey: true },
    });

    survey
      .submitSurvey(
        caseIdOrTaskId,
        protocolId,
        instanceId,
        surveyId,
        patientId,
        questionAnswerSides,
        requestType,
        status,
      )
      .then((response) => {
        dispatch(stopSaving());
        if (response && response.isSuccessful) {
          let nextStatus = 1;
          dispatch({
            type: SUBMIT_SURVEY_ANSWER_SUCCESS,
            data: { status: nextStatus, isCreatingSurvey: false },
          });

          dispatch({
            type: "SET_SNACKBAR_MESSAGE",
            data: { message: "app.survey.saveSurveySuccess", isSuccess: true },
          });

          const navigationPath =
            requestType === "task"
              ? `/tasks/${caseIdOrTaskId}/status/${nextStatus}`
              : `/cases/${caseIdOrTaskId}/survey/${surveyId}/instance/${instanceId}/delegate/${delegateId}/status/${nextStatus}`;

          history.push(navigationPath);
        } else {
          // Log survey submission errors
          fetch(`/api/utility/log`, {
            method: "post",
            headers: {
              "Content-Type": "application/json",
              "utility-log-location": window.location.href,
            },
            body: JSON.stringify({
              message:
                "Submit Survey Error\n" +
                "Survey Url: " +
                window.location.href +
                "\n" +
                "Request Type: " +
                (requestType || "case") +
                "\n" +
                "Response: " +
                JSON.stringify(response) +
                "\n",
              priority: 3,
            }),
          }).catch((err) => console.error("Failed to log error", err));

          dispatch({
            type: "SUBMIT_SURVEY_ANSWER_FAILED",
            data: { isCreatingSurvey: false },
          });
          dispatch({
            type: "SET_SNACKBAR_MESSAGE",
            data: { message: "app.survey.saveSurveyFailed", isSuccess: false },
          });
        }
      })
      .catch((error) => {
        // Log network/exception errors
        dispatch(stopSaving());

        fetch(`/api/utility/log`, {
          method: "post",
          headers: {
            "Content-Type": "application/json",
            "utility-log-location": window.location.href,
          },
          body: JSON.stringify({
            message:
              "Submit Survey Network Error\n" +
              "Survey Url: " +
              window.location.href +
              "\n" +
              "Request Type: " +
              (requestType || "case") +
              "\n" +
              "Error: " +
              (error.message || JSON.stringify(error)) +
              "\n",
            priority: 3,
          }),
        }).catch((err) => console.error("Failed to log error", err));

        dispatch({
          type: "SUBMIT_SURVEY_ANSWER_FAILED",
          data: { isCreatingSurvey: false },
        });
        dispatch({
          type: "SET_SNACKBAR_MESSAGE",
          data: { message: "app.survey.saveSurveyFailed", isSuccess: false },
        });
      });
  };

// Reset isSaving when the user cancels the edit reason modal
export const cancelEditReason = () => (dispatch) => {
  dispatch({ type: "HIDE_EDIT_REASON_MODAL" });
  dispatch(stopSaving());
};

// Handle edit reason confirmation — runs the full submit flow with editReason threaded through
export const confirmEditReason = (editReason) => async (dispatch, getState) => {
  const state = getState().protocol;
  const { pendingSubmit } = state.editReasonModal;

  dispatch({ type: "HIDE_EDIT_REASON_MODAL" });

  if (!pendingSubmit) return;

  // First-time target completion that triggers propagation to a completed source survey.
  // The target itself needs no editReason; only the source (via correctionRequests) does.
  if (pendingSubmit.pendingPropagation) {
    const { correctionRequests, preview, questionAnswerSides } =
      pendingSubmit.pendingPropagation;
    const {
      caseIdOrTaskId,
      protocolId,
      instanceId,
      surveyId,
      patientId,
      delegateId,
      requestType,
      status,
    } = pendingSubmit;

    const correctionRequestsWithReason = correctionRequests.map((cr) => ({
      ...cr,
      editReason,
    }));

    return dispatch({
      type: "SHOW_PROPAGATION_MODAL",
      data: {
        preview,
        correctionRequests: correctionRequestsWithReason,
        submitData: {
          caseIdOrTaskId,
          protocolId,
          instanceId,
          surveyId,
          patientId,
          questionAnswerSides,
          requestType,
          status,
          delegateId,
          editReason: null,
        },
      },
    });
  }

  const {
    caseIdOrTaskId,
    protocolId,
    instanceId,
    surveyId,
    patientId,
    delegateId,
    questionAnswers,
    requestType,
    status,
  } = pendingSubmit;

  const patientCaseSurveyInstances = state.patientCaseSurveyInstances;

  const augmentedAnswers = applyRepeatablePrefillFallbacks(
    questionAnswers,
    state.surveyQuestionHash,
    true,
  );

  let questionAnswerSides = [];
  Object.keys(patientCaseSurveyInstances).map((key) => {
    const side = {
      questionAnswers: augmentedAnswers[parseInt(key)],
      patientCaseSurveyInstanceId:
        patientCaseSurveyInstances[key].patientCaseSurveyInstanceId,
      bilateralAreaId: patientCaseSurveyInstances[key].bilateralAreaId,
      patientCaseSurveyInstanceVersion:
        patientCaseSurveyInstances[key].patientCaseSurveyInstanceVersion,
    };
    questionAnswerSides.push(side);
  });

  const detectedCorrections = detectRepeatableQuestionChanges(
    state.surveyQuestionHash,
    questionAnswers,
    patientCaseSurveyInstances,
    true,
  );

  if (detectedCorrections.length > 0) {
    try {
      const correctionRequests = detectedCorrections.map((c) => ({
        correctionSourceInstanceId: instanceId,
        bilateralAreaId: c.bilateralAreaId,
        questionBaseId: c.questionBaseId,
        questionTitle: c.questionTitle,
        questionType: c.questionType,
        originalValue: c.originalValue,
        correctedValue: c.correctedValue,
        editReason: editReason,
      }));

      const preview =
        requestType === "task"
          ? await survey.previewPropagationForTask(
              caseIdOrTaskId,
              correctionRequests,
            )
          : await survey.previewPropagation(
              caseIdOrTaskId,
              correctionRequests,
            );

      if (preview.requiresPropagation) {
        return dispatch({
          type: "SHOW_PROPAGATION_MODAL",
          data: {
            preview: preview,
            correctionRequests: correctionRequests,
            submitData: {
              caseIdOrTaskId,
              protocolId,
              instanceId,
              surveyId,
              patientId,
              questionAnswerSides,
              requestType,
              status,
              delegateId,
              editReason,
            },
          },
        });
      }
    } catch (error) {
      fetch(`/api/utility/log`, {
        method: "post",
        headers: {
          "Content-Type": "application/json",
          "utility-log-location": window.location.href,
        },
        body: JSON.stringify({
          message:
            "Propagation Preview Error (after edit reason)\n" +
            "Survey Url: " +
            window.location.href +
            "\n" +
            "Request Type: " +
            (requestType || "case") +
            "\n" +
            "Error: " +
            (error.message || JSON.stringify(error)) +
            "\n",
          priority: 3,
        }),
      });
    }
  }

  dispatch({
    type: SUBMIT_SURVEY_ANSWER_REQUESTED,
    data: { isCreatingSurvey: true },
  });

  survey
    .submitSurvey(
      caseIdOrTaskId,
      protocolId,
      instanceId,
      surveyId,
      patientId,
      questionAnswerSides,
      requestType,
      status,
      editReason,
    )
    .then((response) => {
      dispatch(stopSaving());
      if (response && response.isSuccessful) {
        let nextStatus = 1;
        dispatch({
          type: SUBMIT_SURVEY_ANSWER_SUCCESS,
          data: { status: nextStatus, isCreatingSurvey: false },
        });
        dispatch({
          type: "SET_SNACKBAR_MESSAGE",
          data: { message: "app.survey.saveSurveySuccess", isSuccess: true },
        });

        const navigationPath =
          requestType === "task"
            ? `/tasks/${caseIdOrTaskId}/status/${nextStatus}`
            : `/cases/${caseIdOrTaskId}/survey/${surveyId}/instance/${instanceId}/delegate/${delegateId}/status/${nextStatus}`;

        history.push(navigationPath);
      } else {
        dispatch({
          type: "SUBMIT_SURVEY_ANSWER_FAILED",
          data: { isCreatingSurvey: false },
        });
        dispatch({
          type: "SET_SNACKBAR_MESSAGE",
          data: { message: "app.survey.saveSurveyFailed", isSuccess: false },
        });
      }
    })
    .catch((error) => {
      dispatch(stopSaving());
      dispatch({
        type: "SUBMIT_SURVEY_ANSWER_FAILED",
        data: { isCreatingSurvey: false },
      });
      dispatch({
        type: "SET_SNACKBAR_MESSAGE",
        data: { message: "app.survey.saveSurveyFailed", isSuccess: false },
      });
    });
};

// Handle propagation confirmation
export const confirmPropagation =
  (submitData, correctionRequests) => async (dispatch) => {
    dispatch({
      type: SUBMIT_SURVEY_ANSWER_REQUESTED,
      data: { isCreatingSurvey: true },
    });

    try {
      // Submit the survey first
      const response = await survey.submitSurvey(
        submitData.caseIdOrTaskId,
        submitData.protocolId,
        submitData.instanceId,
        submitData.surveyId,
        submitData.patientId,
        submitData.questionAnswerSides,
        submitData.requestType,
        submitData.status,
        submitData.editReason,
      );

      if (response && response.isSuccessful) {
        // Propagate changes using the appropriate endpoint
        try {
          const propagateFn =
            submitData.requestType === "task"
              ? survey.propagateCorrectionsForTask
              : survey.propagateCorrections;

          const propagationResult = await propagateFn(
            submitData.caseIdOrTaskId,
            correctionRequests,
          );

          const totalAffected = propagationResult.affectedSurveys?.length || 0;

          dispatch(stopSaving());
          dispatch({
            type: SUBMIT_SURVEY_ANSWER_SUCCESS,
            data: { status: 1, isCreatingSurvey: false },
          });

          const messageKey =
            totalAffected === 1
              ? "app.repeatableQuestions.propagation.successSingular"
              : "app.repeatableQuestions.propagation.successPlural";

          dispatch({
            type: "SET_SNACKBAR_MESSAGE",
            data: {
              message: messageKey,
              messageValues: { count: totalAffected },
              isSuccess: true,
            },
          });

          const navigationPath =
            submitData.requestType === "task"
              ? `/tasks/${submitData.caseIdOrTaskId}/status/1`
              : `/cases/${submitData.caseIdOrTaskId}/survey/${submitData.surveyId}/instance/${submitData.instanceId}/delegate/${submitData.delegateId}/status/1`;

          history.push(navigationPath);
        } catch (error) {
          console.error("Propagation failed", error);

          // Log propagation errors
          fetch(`/api/utility/log`, {
            method: "post",
            headers: {
              "Content-Type": "application/json",
              "utility-log-location": window.location.href,
            },
            body: JSON.stringify({
              message:
                "Propagation Execution Error\n" +
                "Survey Url: " +
                window.location.href +
                "\n" +
                "Request Type: " +
                (submitData.requestType || "case") +
                "\n" +
                "Error: " +
                (error.message || JSON.stringify(error)) +
                "\n",
              priority: 3,
            }),
          }).catch((err) => console.error("Failed to log error", err));

          dispatch(stopSaving());
          dispatch({
            type: "SET_SNACKBAR_MESSAGE",
            data: {
              message: "app.repeatableQuestions.propagation.partialSuccess", // ← Just key
              isSuccess: false,
            },
          });

          const navigationPath =
            submitData.requestType === "task"
              ? `/tasks/${submitData.caseIdOrTaskId}/status/1`
              : `/cases/${submitData.caseIdOrTaskId}/survey/${submitData.surveyId}/instance/${submitData.instanceId}/delegate/${submitData.delegateId}/status/1`;

          history.push(navigationPath);
        }
      } else {
        // Log survey submission failure in confirm propagation
        fetch(`/api/utility/log`, {
          method: "post",
          headers: {
            "Content-Type": "application/json",
            "utility-log-location": window.location.href,
          },
          body: JSON.stringify({
            message:
              "Confirm Propagation - Submit Survey Error\n" +
              "Survey Url: " +
              window.location.href +
              "\n" +
              "Request Type: " +
              (submitData.requestType || "case") +
              "\n" +
              "Response: " +
              JSON.stringify(response) +
              "\n",
            priority: 3,
          }),
        }).catch((err) => console.error("Failed to log error", err));

        dispatch(stopSaving());
        dispatch({
          type: SUBMIT_SURVEY_ANSWER_FAILED,
          data: { isCreatingSurvey: false },
        });
        dispatch({
          type: "SET_SNACKBAR_MESSAGE",
          data: {
            message: "app.survey.saveSurveyFailed",
            isSuccess: false,
          },
        });
      }
    } catch (error) {
      // Log unexpected errors in confirm propagation
      console.error("Unexpected error in confirmPropagation", error);

      fetch(`/api/utility/log`, {
        method: "post",
        headers: {
          "Content-Type": "application/json",
          "utility-log-location": window.location.href,
        },
        body: JSON.stringify({
          message:
            "Confirm Propagation - Unexpected Error\n" +
            "Survey Url: " +
            window.location.href +
            "\n" +
            "Request Type: " +
            (submitData.requestType || "case") +
            "\n" +
            "Error: " +
            (error.message || JSON.stringify(error)) +
            "\n",
          priority: 3,
        }),
      }).catch((err) => console.error("Failed to log error", err));

      dispatch(stopSaving());
      dispatch({
        type: SUBMIT_SURVEY_ANSWER_FAILED,
        data: { isCreatingSurvey: false },
      });
      dispatch({
        type: "SET_SNACKBAR_MESSAGE",
        data: {
          message: "app.survey.saveSurveyFailed",
          isSuccess: false,
        },
      });
    }
  };

/**
 * Detect repeatable question changes for ALL sides
 * Handles both bilateral and non-bilateral protocols
 *
 * @param {Object} surveyQuestionHash - Redux state with questions for each side
 * @param {Array} questionAnswers - User-submitted answers for each side
 * @param {Object} patientCaseSurveyInstances - PCSI data with bilateral area info
 * @param {boolean} isReOpened - Whether this is a re-opened/edited survey
 * @returns {Array} Array of correction requests with correct PCSI for each side
 */
function detectRepeatableQuestionChanges(
  surveyQuestionHash,
  questionAnswers,
  patientCaseSurveyInstances,
  isReOpened,
) {
  const detectedCorrections = [];

  // Early exit if no question hash
  if (!surveyQuestionHash) {
    return detectedCorrections;
  }

  // Determine how many sides to check (bilateral = 2, non-bilateral = 1)
  const sidesCount = Object.keys(surveyQuestionHash).length;

  // Loop through ALL sides (handles both bilateral and non-bilateral)
  for (let sideIndex = 0; sideIndex < sidesCount; sideIndex++) {
    const questionsForThisSide = surveyQuestionHash[sideIndex];

    if (!questionsForThisSide) {
      continue;
    }

    const allQuestions = Object.values(questionsForThisSide);
    const repeatableQuestions = allQuestions.filter(
      (node) => node.data?.isRepeatable,
    );

    repeatableQuestions.forEach((questionNode) => {
      const question = questionNode.data;

      if (!question.id) return;

      // Verify repeatableMetadata exists and matches type
      if (question.isRepeatable) {
        const metadata = question.repeatableMetadata;

        // If metadata exists, verify the types match
        if (metadata && metadata.questionType !== question.questionType) {
          return; // Skip this question
        }

        // If no metadata but marked repeatable, that's suspicious
        if (!metadata) {
          return; // Skip this question
        }
      }

      // Get current (submitted) value from the CORRECT side
      const currentAnswer = getCurrentAnswerValue(
        question,
        questionAnswers[sideIndex],
      );

      // Get original (prefilled) value from source survey
      // For both new and reopened surveys, compare against the SOURCE value
      const originalValue = question.preFilledValue;

      // Skip empty → empty
      const isCurrentEmpty = isValueEmpty(currentAnswer, question.questionType);
      const isOriginalEmpty = isValueEmpty(
        originalValue,
        question.questionType,
      );

      if (isCurrentEmpty && isOriginalEmpty) return; // empty → empty
      if (isCurrentEmpty) return;

      // Compare values (handles all question types)
      const hasChanged = detectChange(
        currentAnswer,
        originalValue,
        question.questionType,
      );

      // Record correction if changed (only when new value is non-null)
      if (hasChanged && originalValue != null) {
        // Get correct PCSI for this side
        const pcsiInfo = patientCaseSurveyInstances[sideIndex];

        const correction = {
          questionBaseId: question.id,
          questionTitle: question.title || `Question ${question.id}`,
          questionType: question.questionType,
          originalValue,
          correctedValue: currentAnswer,
          bilateralAreaId: pcsiInfo?.bilateralAreaId,
        };
        detectedCorrections.push(correction);
      }
    });
  }
  return detectedCorrections;
}

function applyRepeatablePrefillFallbacks(
  questionAnswers,
  surveyQuestionHash,
  isReOpened,
) {
  return questionAnswers.map((sideAnswers, sideIndex) => {
    if (!sideAnswers || !surveyQuestionHash?.[sideIndex]) return sideAnswers;

    const augmented = [...sideAnswers];
    const allQuestions = Object.values(surveyQuestionHash[sideIndex]);
    const repeatableQuestions = allQuestions.filter(
      (node) => node.data?.isRepeatable && node.data?.preFilledValue != null,
    );

    repeatableQuestions.forEach((node) => {
      const question = node.data;
      if (!question.id || !question.preFilledValue) return;

      const currentAnswer = getCurrentAnswerValue(question, sideAnswers);
      const isEmpty = isValueEmpty(currentAnswer, question.questionType);

      if (!isEmpty) return;

      switch (question.questionType) {
        case 1:
        case 5:
        case 6:
        case 7:
        case 9:
          if (
            !augmented.some(
              (a) =>
                a.questionId === question.id &&
                a.questionType === question.questionType,
            )
          ) {
            augmented.push({
              questionId: question.id,
              questionType: question.questionType,
              answer: question.preFilledValue,
            });
          }
          break;

        case 2:
          if (
            !augmented.some(
              (a) => a.questionId === question.id && a.questionType === 2,
            )
          ) {
            question.preFilledValue
              .split(";")
              .filter(Boolean)
              .forEach((id) => {
                augmented.push({
                  questionId: question.id,
                  questionType: 2,
                  answer: id,
                });
              });
          }
          break;

        case 3:
          const categoryIds = question.preFilledValue
            .split(";")
            .filter(Boolean)
            .map((pair) => parseInt(pair.split(":")[0]));

          // Remove any partial answers the user entered for this group
          const filtered = augmented.filter(
            (a) =>
              !(a.questionType === 3 && categoryIds.includes(a.questionId)),
          );
          augmented.length = 0;
          augmented.push(...filtered);

          // Inject full prefill for all categories
          question.preFilledValue
            .split(";")
            .filter(Boolean)
            .forEach((pair) => {
              const [catId, val] = pair.split(":");
              augmented.push({
                questionId: parseInt(catId),
                questionType: 3,
                answer: val || null,
              });
            });
          break;

        default:
          break;
      }
    });

    return augmented;
  });
}

/**
 * Check if a value is effectively empty based on question type
 */
function isValueEmpty(value, questionType) {
  // Null, undefined, or empty string is always empty
  if (value == null || value === "") {
    return true;
  }

  // Type 1 - Single Value (Radio): Empty if null or empty string
  if (questionType === 1) {
    return value === "" || value == null;
  }

  // Type 2 - Multiple Choice: Empty if null or empty string
  if (questionType === 2) {
    return value === "" || value == null;
  }

  // Type 3 - Grouped Questions: Check if any category values are empty
  // Format when filled: "632:1;633:2;634:3"
  // Format when cleared: "632:;633:;634:" or ""
  if (questionType === 3) {
    if (!value || value === "") return true;

    try {
      const pairs = value.split(";");
      // Check if any category values are empty
      const anyEmpty = pairs.some((pair) => {
        const [, val] = pair.split(":");
        return val === "" || val === undefined || val === null;
      });
      return anyEmpty;
    } catch (e) {
      console.error("Error checking grouped question empty state:", e);
      return false;
    }
  }

  // Type 5 - Text: Empty if null or empty string
  if (questionType === 5) {
    return String(value).trim() === "" || value == null;
  }

  // Type 6 - Number: Empty if null, empty string, or NaN
  if (questionType === 6) {
    if (String(value).trim() === "" || value == null) return true;
    const num = parseFloat(value);
    return isNaN(num);
  }

  // Type 7 - Date: Empty if null or empty string
  if (questionType === 7) {
    return value === "" || value == null;
  }

  // Type 9 - Analog Scale: Empty if null, empty string, or NaN
  if (questionType === 9) {
    if (value === "" || value == null) return true;
    const num = parseFloat(value);
    return isNaN(num);
  }

  // Type 10/11 - Files: Check if file data is empty
  if (questionType === 10 || questionType === 11) {
    if (!value || value === "") return true;

    try {
      const fileData = typeof value === "string" ? JSON.parse(value) : value;
      const fileId = fileData?.value || fileData?.url;
      return !fileId || fileId === "";
    } catch (e) {
      console.error("Error checking file empty state:", e);
      return false;
    }
  }

  // Default: not empty
  return false;
}

/**
 * Compare values with proper normalization for grouped questions
 */
function detectChange(currentValue, originalValue, questionType) {
  if (originalValue == null || currentValue == null) {
    return false;
  }

  // Normalize analog scale values (stored as strings but are numbers)
  if (questionType === 9) {
    const normalizedCurrent = parseFloat(currentValue);
    const normalizedOriginal = parseFloat(originalValue);
    if (isNaN(normalizedCurrent) || isNaN(normalizedOriginal)) {
      return currentValue !== originalValue;
    }
    return normalizedCurrent !== normalizedOriginal;
  }

  // Normalize multiple choice before comparing
  if (questionType === 2) {
    const normalizedCurrent = normalizeMultipleChoiceValue(currentValue);
    const normalizedOriginal = normalizeMultipleChoiceValue(originalValue);
    return normalizedCurrent !== normalizedOriginal;
  }

  // Normalize numbers before comparing
  if (questionType === 6) {
    const normalizedCurrent = parseFloat(currentValue);
    const normalizedOriginal = parseFloat(originalValue);
    if (isNaN(normalizedCurrent) || isNaN(normalizedOriginal)) {
      return currentValue !== originalValue;
    }
    return normalizedCurrent !== normalizedOriginal;
  }

  // Normalize grouped questions before comparing
  if (questionType === 3) {
    const normalizedCurrent = normalizeGroupedValue(currentValue);
    const normalizedOriginal = normalizeGroupedValue(originalValue);
    return normalizedCurrent !== normalizedOriginal;
  }

  // Special handling for dates - normalize before comparing
  if (questionType === 7) {
    const normalizedCurrent = normalizeDateValue(currentValue);
    const normalizedOriginal = normalizeDateValue(originalValue);
    return normalizedCurrent !== normalizedOriginal;
  }

  // Special handling for files - compare file identifiers, not full JSON
  if (questionType === 10 || questionType === 11) {
    try {
      const currentData =
        typeof currentValue === "string"
          ? JSON.parse(currentValue)
          : currentValue;
      const originalData =
        typeof originalValue === "string"
          ? JSON.parse(originalValue)
          : originalValue;

      const currentFileId = currentData?.value || currentData?.url;
      const originalFileId = originalData?.value || originalData?.url;

      return currentFileId !== originalFileId;
    } catch (e) {
      console.error("Error comparing file values:", e);
      return currentValue !== originalValue;
    }
  }

  // Standard string comparison for all other types
  return currentValue !== originalValue;
}

function normalizeMultipleChoiceValue(value) {
  if (!value) return value;

  try {
    const answers = value
      .split(";")
      .map((a) => parseInt(a))
      .sort((a, b) => a - b)
      .join(";");
    return answers;
  } catch (e) {
    console.error("Error normalizing multiple choice value:", e);
    return value;
  }
}

/**
 * Normalize grouped question format for consistent comparison
 * Sorts category pairs by ID to handle ordering differences
 */
function normalizeGroupedValue(value) {
  if (!value) return value;

  try {
    const pairs = value.split(";");
    const sorted = pairs
      .map((pair) => {
        const [id, val] = pair.split(":");
        const parsedId = parseInt(id);

        // Handle empty or invalid values
        if (val === "" || val === undefined || val === null) {
          return { id: parsedId, value: null };
        }

        const parsedVal = parseInt(val);
        if (isNaN(parsedVal)) {
          console.warn(`Invalid value in grouped normalization: ${val}`);
          return { id: parsedId, value: null };
        }

        return { id: parsedId, value: parsedVal };
      })
      .sort((a, b) => a.id - b.id)
      .map((item) => {
        return item.value === null ? `${item.id}:` : `${item.id}:${item.value}`;
      });

    return sorted.join(";");
  } catch (e) {
    console.error("Error normalizing grouped value:", e);
    return value;
  }
}

function getCurrentAnswerValue(question, submittedAnswers) {
  if (!submittedAnswers) return null;

  switch (question.questionType) {
    case 1: // Single Value
    case 5: // Text
    case 6: // Number
    case 9: // Analog
      return getSingleAnswer(submittedAnswers, question);

    case 2: // Multiple Value
      return getMultipleAnswers(submittedAnswers, question);

    case 3: // Grouped
      return getGroupedAnswers(submittedAnswers, question);

    case 7: // Date
      return getSingleAnswer(submittedAnswers, question);

    case 10: // Image File
    case 11: // PDF File
      return getFileAnswer(submittedAnswers, question);

    default:
      return null;
  }
}

function getSingleAnswer(submittedAnswers, question) {
  const answer = submittedAnswers.find(
    (a) =>
      a.questionId === question.id && a.questionType === question.questionType,
  );

  return answer?.answer?.toString() || null;
}

function getMultipleAnswers(submittedAnswers, question) {
  const answers = submittedAnswers
    .filter((a) => a.questionId === question.id && a.questionType === 2)
    .map((a) => String(a.answer))
    .sort((a, b) => parseInt(a) - parseInt(b));

  return answers.length > 0 ? answers.join(";") : null;
}

function getGroupedAnswers(submittedAnswers, question) {
  // Try to get category IDs from question.categories first
  let categoryIds;

  if (question.categories && question.categories.length > 0) {
    categoryIds = question.categories.map((c) => c.id);
  } else if (question.preFilledValue) {
    categoryIds = question.preFilledValue
      .split(";")
      .map((pair) => parseInt(pair.split(":")[0]));
  } else {
    return null;
  }

  try {
    const categoryAnswers = submittedAnswers
      .filter((a) => a.questionType === 3 && categoryIds.includes(a.questionId))
      .sort((a, b) => a.questionId - b.questionId)
      .map((a) => {
        // Handle null/undefined/empty values
        let value = a.answer;
        if (value === null || value === undefined || value === "") {
          return `${a.questionId}:`; // Empty value format
        }
        // Parse and validate the number
        const numValue = parseInt(value);
        if (isNaN(numValue)) {
          return `${a.questionId}:`;
        }
        return `${a.questionId}:${numValue}`;
      })
      .join(";");

    return categoryAnswers || null;
  } catch (e) {
    console.error("Error processing grouped question:", e);
    return null;
  }
}

function getFileAnswer(submittedAnswers, question) {
  const fileAnswer = submittedAnswers.find(
    (a) =>
      a.questionId === question.id && a.questionType === question.questionType,
  );

  if (!fileAnswer) {
    return question.preFilledValue || null;
  }

  if (fileAnswer.notChanged === true) {
    return question.preFilledValue;
  }

  if (fileAnswer.answer) {
    try {
      return JSON.stringify({
        value: fileAnswer.answer,
        name: fileAnswer.fileName,
        mime: fileAnswer.mimeType,
      });
    } catch (e) {
      console.error("Error creating file answer:", e);
      return null;
    }
  }

  return null;
}

function normalizeDateValue(dateStr) {
  if (!dateStr) return dateStr;

  try {
    // If ISO format (YYYY-MM-DD), convert to M/D/YYYY
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [year, month, day] = dateStr.split("-");
      return `${parseInt(month, 10)}/${parseInt(day, 10)}/${year}`;
    }

    // If already MM/DD/YYYY or M/D/YYYY format, normalize to M/D/YYYY
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateStr)) {
      const parts = dateStr.split("/");
      if (parts.length === 3) {
        const month = parseInt(parts[0], 10);
        const day = parseInt(parts[1], 10);
        const year = parts[2];
        return `${month}/${day}/${year}`;
      }
    }

    return dateStr;
  } catch (e) {
    console.error("Error normalizing date:", e);
    return dateStr;
  }
}

const FETCH_OBSERVATIONAL_PROTOCOL_REQUESTED =
  "FETCH_OBSERVATIONAL_PROTOCOL_REQUESTED";
const FETCH_OBSERVATIONAL_PROTOCOL_SUCCESS =
  "FETCH_OBSERVATIONAL_PROTOCOL_SUCCESS";
const FETCH_OBSERVATIONAL_PROTOCOL_FAILED =
  "FETCH_OBSERVATIONAL_PROTOCOL_FAILED";

export const fetchProtocolByVersion = (id, versionId) => (dispatch) => {
  if (!id) {
    return dispatch({ type: FETCH_OBSERVATIONAL_PROTOCOL_SUCCESS, data: null });
  }
  dispatch({ type: FETCH_OBSERVATIONAL_PROTOCOL_REQUESTED, data: {} });
  protocol
    .getObservationalProtocolByVersion(id, versionId)
    .then((observationProtocol) => {
      if (observationProtocol && observationProtocol.isSuccessful) {
        return dispatch({
          type: FETCH_OBSERVATIONAL_PROTOCOL_SUCCESS,
          data: observationProtocol,
        });
      } else {
        return dispatch({
          type: "SET_SNACKBAR_MESSAGE",
          data: {
            message: "app.protocol.fetchProtocolFailed",
            isSuccess: false,
          },
        });
      }
    });
  return;
};
// TODO: DEPRECATED
export const fetchProtocolByCircle = (id, circleId) => (dispatch) => {
  if (!id) {
    return dispatch({ type: FETCH_OBSERVATIONAL_PROTOCOL_SUCCESS, data: null });
  }
  dispatch({ type: FETCH_OBSERVATIONAL_PROTOCOL_REQUESTED, data: {} });
  protocol
    .getObservationalProtocolByCircle(id, circleId)
    .then((observationProtocol) => {
      if (observationProtocol && observationProtocol.isSuccessful) {
        return dispatch({
          type: FETCH_OBSERVATIONAL_PROTOCOL_SUCCESS,
          data: observationProtocol,
        });
      } else {
        return dispatch({
          type: "SET_SNACKBAR_MESSAGE",
          data: {
            message: "app.protocol.fetchProtocolFailed",
            isSuccess: false,
          },
        });
      }
    });
  return;
};

const FETCH_OBSERVATIONAL_PROTOCOLS_REQUESTED =
  "FETCH_OBSERVATIONAL_PROTOCOLS_REQUESTED";
const FETCH_OBSERVATIONAL_PROTOCOLS_SUCCESS =
  "FETCH_OBSERVATIONAL_PROTOCOLS_SUCCESS";
const FETCH_OBSERVATIONAL_PROTOCOLS_FAILED =
  "FETCH_OBSERVATIONAL_PROTOCOLS_FAILED";
export const fetchProtocols = () => (dispatch) => {
  dispatch({ type: FETCH_OBSERVATIONAL_PROTOCOLS_REQUESTED, data: {} });
  protocol.getObservationalProtocols().then((observationProtocols) => {
    if (observationProtocols && observationProtocols.isSuccessful) {
      return dispatch({
        type: FETCH_OBSERVATIONAL_PROTOCOLS_SUCCESS,
        data: observationProtocols.data,
      });
    } else {
      return dispatch({
        type: "SET_SNACKBAR_MESSAGE",
        data: {
          message: "app.protocol.fetchProtocolsFailed",
          isSuccess: false,
        },
      });
    }
  });
  return;
};

const FETCH_SURVEY_REQUESTED = "FETCH_SURVEY_REQUESTED";
const FETCH_SURVEY_SUCCESS = "FETCH_SURVEY_SUCCESS";
const FETCH_SURVEY_FAILED = "FETCH_SURVEY_FAILED";

export const fetchSurvey = (protocolId, surveyId) => (dispatch) => {
  dispatch({ type: FETCH_SURVEY_REQUESTED, data: {} });

  protocol.getSurvey(protocolId, surveyId).then((survey) => {
    if (survey && survey.isSuccessful) {
      let questions = new Array(0);
      let hash = {};
      let container = new ListContainer();

      processSurveyQuestions(survey, container, questions, hash);
      return dispatch({
        type: FETCH_SURVEY_SUCCESS,
        data: { survey: survey, list: container, flat: questions, nodes: hash },
      });
    } else {
      return dispatch({
        type: "SET_SNACKBAR_MESSAGE",
        data: { message: "app.protocol.fetchSurveyFailed", isSuccess: false },
      });
    }
  });
};

const FETCH_SURVEY_VALUES_REQUESTED = "FETCH_SURVEY_VALUES_REQUESTED";
const FETCH_SURVEY_VALUES_SUCCESS = "FETCH_SURVEY_VALUES_SUCCESS";
const FETCH_SURVEY_VALUES_FAILED = "FETCH_SURVEY_VALUES_FAILED";

export const fetchSurveyValues =
  (protocolId, surveyId, instanceId, caseId, patientId, status) =>
  (dispatch) => {
    dispatch({
      type: FETCH_SURVEY_VALUES_REQUESTED,
      data: { isCreatingSurvey: false },
    });

    protocol
      .getSurveyValues(protocolId, surveyId, instanceId, caseId, patientId)
      .then((survey) => {
        if (survey && survey.isSuccessful) {
          let questionContainerList = [];
          let hashList = [];
          let containerList = [];
          let revisionsList = [];
          let patientCaseSurveyInstances = {};

          survey.answeredQuestions
            .sort((a, b) => (a.bilateralAreaId > b.bilateralAreaId ? 1 : -1))
            .map((answeredQuestions, index) => {
              let questionContainer = new Array(0);
              let hash = {};
              let container = new ListContainer();

              processSurveyQuestions(
                answeredQuestions.questions,
                container,
                questionContainer,
                hash,
              );

              questionContainerList.push(questionContainer);
              hashList.push(hash);
              containerList.push(container);
              revisionsList.push(answeredQuestions.revisions);
              patientCaseSurveyInstances[`${index}`] = {
                patientCaseSurveyInstanceId:
                  answeredQuestions.patientCaseSurveyInstanceId,
                bilateralAreaId: answeredQuestions.bilateralAreaId,
                patientCaseSurveyInstanceVersion:
                  answeredQuestions.patientCaseSurveyInstanceVersion,
              };
            });

          let bilateralRevisions = getBilateralRevisions(revisionsList);

          return dispatch({
            type: FETCH_SURVEY_VALUES_SUCCESS,
            data: {
              surveyId: surveyId,
              survey: survey,
              list: containerList,
              flat: questionContainerList,
              nodes: hashList,
              status: status,
              revisions: bilateralRevisions,
              patientCaseSurveyInstances: patientCaseSurveyInstances,
            },
          });
        } else {
          return dispatch({
            type: "SET_SNACKBAR_MESSAGE",
            data: {
              message: "app.protocol.fetchSurveyFailed",
              isSuccess: false,
            },
          });
        }
      });
  };

export const fetchSurveyValuesByTaskId =
  (surveyId, taskId, status) => (dispatch) => {
    dispatch({
      type: FETCH_SURVEY_VALUES_REQUESTED,
      data: { isCreatingSurvey: false },
    });

    protocol.getTaskSurveyValues(taskId).then((survey) => {
      if (survey && survey.isSuccessful) {
        let questionContainerList = [];
        let hashList = [];
        let containerList = [];
        let revisionsList = [];
        let patientCaseSurveyInstances = {};

        survey.answeredQuestions
          .sort((a, b) => (a.bilateralAreaId > b.bilateralAreaId ? 1 : -1))
          .map((answeredQuestions, index) => {
            let questionContainer = new Array(0);
            let hash = {};
            let container = new ListContainer();

            processSurveyQuestions(
              answeredQuestions.questions,
              container,
              questionContainer,
              hash,
            );

            questionContainerList.push(questionContainer);
            hashList.push(hash);
            containerList.push(container);
            revisionsList.push(answeredQuestions.revisions);
            patientCaseSurveyInstances[`${index}`] = {
              patientCaseSurveyInstanceId:
                answeredQuestions.patientCaseSurveyInstanceId,
              bilateralAreaId: answeredQuestions.bilateralAreaId,
              patientCaseSurveyInstanceVersion:
                answeredQuestions.patientCaseSurveyInstanceVersion,
            };
          });

        let bilateralRevisions = getBilateralRevisions(revisionsList);

        return dispatch({
          type: FETCH_SURVEY_VALUES_SUCCESS,
          data: {
            surveyId: surveyId,
            survey: survey,
            list: containerList,
            flat: questionContainerList,
            nodes: hashList,
            status: status,
            revisions: bilateralRevisions,
            patientCaseSurveyInstances: patientCaseSurveyInstances,
          },
        });
      } else {
        return dispatch({
          type: "SET_SNACKBAR_MESSAGE",
          data: { message: "app.protocol.fetchSurveyFailed", isSuccess: false },
        });
      }
    });
  };

const processSurveyQuestions = (
  surveyQuestions,
  container,
  questionContainer,
  hash,
) => {
  if (surveyQuestions) {
    surveyQuestions
      .sort((a, b) => (a.sortOrder > b.sortOrder ? 1 : -1))
      .map((r) => {
        container.getList().append(r);
      });
    container.getList().traverse((r) => {
      questionContainer.push(r.data.uniqueId);
      hash[r.data.uniqueId] = r;
    });
  }
};

const SAVE_OBSERVATIONAL_PROTOCOL_REQUESTED =
  "SAVE_OBSERVATIONAL_PROTOCOL_REQUESTED";
const SAVE_OBSERVATIONAL_PROTOCOL_SUCCESS =
  "SAVE_OBSERVATIONAL_PROTOCOL_SUCCESS";
const SAVE_OBSERVATIONAL_PROTOCOL_FAILED = "SAVE_OBSERVATIONAL_PROTOCOL_FAILED";

export const editProtocolAlias =
  (protocolId, circleId, alias) => (dispatch) => {
    dispatch({ type: SAVE_OBSERVATIONAL_PROTOCOL_REQUESTED, data: {} });

    protocol
      .saveObservationalProtocolAlias(protocolId, circleId, alias)
      .then((reponse) => {
        if (reponse && reponse.isSuccessful) {
          dispatch(hideModal());

          dispatch({
            type: "SET_SNACKBAR_MESSAGE",
            data: { message: "app.general.success", isSuccess: true },
          });

          dispatch(stopSaving());

          return dispatch(fetchProtocolByCircle(protocolId, circleId));
        } else {
          dispatch(stopSaving());

          return dispatch({
            type: "SET_SNACKBAR_MESSAGE",
            data: {
              message: "app.protocol.saveProtocolFailed",
              isSuccess: false,
            },
          });
        }
      });
    return;
  };

const FETCH_SURVEY_QUESTIONS_REQUESTED = "FETCH_SURVEY_QUESTIONS_REQUESTED";
const FETCH_SURVEY_QUESTIONS_SUCCESS = "FETCH_SURVEY_QUESTIONS_SUCCESS";
export const fetchSurveyQuestionsByProtocolVersion =
  (observationProtocolSurveyId, protocolVersionId) => (dispatch) => {
    dispatch({ type: FETCH_SURVEY_QUESTIONS_REQUESTED, data: {} });

    protocol
      .getSurveyQuestionsByProtocolVersion(
        observationProtocolSurveyId,
        protocolVersionId,
      )
      .then((survey) => {
        if (survey && survey.isSuccessful) {
          let questionContainerList = [];
          let hashList = [];
          let containerList = [];
          let revisionsList = [];
          let patientCaseSurveyInstances = {};

          survey.answeredQuestions.map((answeredQuestions, index) => {
            let questionContainer = new Array(0);
            let hash = {};
            let container = new ListContainer();

            processSurveyQuestions(
              answeredQuestions.questions,
              container,
              questionContainer,
              hash,
            );

            questionContainerList.push(questionContainer);
            hashList.push(hash);
            containerList.push(container);
            revisionsList.push(answeredQuestions.revisions);
            patientCaseSurveyInstances[`${index}`] = {
              patientCaseSurveyInstanceId:
                answeredQuestions.patientCaseSurveyInstanceId,
              bilateralAreaId: answeredQuestions.bilateralAreaId,
              patientCaseSurveyInstanceVersion:
                answeredQuestions.patientCaseSurveyInstanceVersion,
            };
          });

          return dispatch({
            type: FETCH_SURVEY_SUCCESS,
            data: {
              survey: survey,
              list: containerList,
              flat: questionContainerList,
              nodes: hashList,
              patientCaseSurveyInstances: patientCaseSurveyInstances,
            },
          });
        } else {
          return dispatch({
            type: "SET_SNACKBAR_MESSAGE",
            data: {
              message: "app.protocol.fetchSurveyFailed",
              isSuccess: false,
            },
          });
        }
      });
    return;
  };
// TODO: DEPRECATED
export const fetchSurveyQuestionsByCircle =
  (observationProtocolSurveyId, circleId) => (dispatch) => {
    dispatch({ type: FETCH_SURVEY_QUESTIONS_REQUESTED, data: {} });

    protocol
      .getSurveyQuestionsByCircle(observationProtocolSurveyId, circleId)
      .then((survey) => {
        if (survey && survey.isSuccessful) {
          let questions = new Array(0);
          let hash = {};
          let container = new ListContainer();

          processSurveyQuestions(survey, container, questions, hash);
          return dispatch({
            type: FETCH_SURVEY_SUCCESS,
            data: {
              survey: survey,
              list: container,
              flat: questions,
              nodes: hash,
            },
          });
        } else {
          return dispatch({
            type: "SET_SNACKBAR_MESSAGE",
            data: {
              message: "app.protocol.fetchSurveyFailed",
              isSuccess: false,
            },
          });
        }
      });
    return;
  };

const FETCH_PROTOCOL_AUTOCOMPLETE_REQUESTED =
  "FETCH_PROTOCOL_AUTOCOMPLETE_REQUESTED";
const FETCH_PROTOCOL_AUTOCOMPLETE_SUCCESS =
  "FETCH_PROTOCOL_AUTOCOMPLETE_SUCCESS";
const FETCH_PROTOCOL_AUTOCOMPLETE_FAILED = "FETCH_PROTOCOL_AUTOCOMPLETE_FAILED";

export const fetchProtocolAutoComplete =
  (searchPhrase, noMatchesText) => (dispatch) => {
    dispatch({ type: FETCH_PROTOCOL_AUTOCOMPLETE_REQUESTED, data: {} });

    protocol.fetchProtocolAutoComplete(searchPhrase).then((response) => {
      if (response && response.isSuccessful) {
        {
          if (response.protocols && response.protocols.length > 0) {
            return dispatch({
              type: FETCH_PROTOCOL_AUTOCOMPLETE_SUCCESS,
              data: response.protocols,
            });
          } else {
            return dispatch({
              type: FETCH_PROTOCOL_AUTOCOMPLETE_SUCCESS,
              data: [{ id: -1, name: noMatchesText }],
            });
          }
        }
      } else {
        return dispatch({ type: FETCH_PROTOCOL_AUTOCOMPLETE_FAILED, data: {} });
      }
    });
    return;
  };

const FETCH_CHILD_PROTOCOL_AUTOCOMPLETE_REQUESTED =
  "FETCH_CHILD_PROTOCOL_AUTOCOMPLETE_REQUESTED";
const FETCH_CHILD_PROTOCOL_AUTOCOMPLETE_SUCCESS =
  "FETCH_CHILD_PROTOCOL_AUTOCOMPLETE_SUCCESS";
const FETCH_CHILD_PROTOCOL_AUTOCOMPLETE_FAILED =
  "FETCH_CHILD_PROTOCOL_AUTOCOMPLETE_FAILED";

export const fetchChildProtocolAutoComplete =
  (id, searchPhrase) => (dispatch) => {
    dispatch({ type: FETCH_CHILD_PROTOCOL_AUTOCOMPLETE_REQUESTED, data: {} });

    protocol
      .fetchChildProtocolAutoComplete(id, searchPhrase)
      .then((protocols) => {
        if (protocols) {
          return dispatch({
            type: FETCH_CHILD_PROTOCOL_AUTOCOMPLETE_SUCCESS,
            data: protocols.protocols,
          });
        } else {
          return dispatch({
            type: FETCH_CHILD_PROTOCOL_AUTOCOMPLETE_FAILED,
            data: {},
          });
        }
      });
    return;
  };

export const RESET_SURVEY = "RESET_SURVEY";
export const resetSurvey = () => (dispatch) => {
  dispatch({ type: RESET_SURVEY, data: {} });
};

const CHANGE_SURVEY_COMPLETED_DATE_REQUESTED =
  "CHANGE_SURVEY_COMPLETED_DATE_REQUESTED";
const CHANGE_SURVEY_COMPLETED_DATE_SUCCESS =
  "CHANGE_SURVEY_COMPLETED_DATE_SUCCESS";
const CHANGE_SURVEY_COMPLETED_DATE_FAILED =
  "CHANGE_SURVEY_COMPLETED_DATE_FAILED";

export const changeSurveyCompletedDate =
  (caseId, instanceId, newDate, requestType, taskId) => (dispatch) => {
    dispatch({ type: CHANGE_SURVEY_COMPLETED_DATE_REQUESTED, data: {} });
    survey
      .changeSurveyCompletedDate(caseId, instanceId, newDate)
      .then((response) => {
        if (response && response.isSuccessful) {
          dispatch(hideModal());
          dispatch({ type: CHANGE_SURVEY_COMPLETED_DATE_SUCCESS, data: {} });

          dispatch({
            type: "SET_SNACKBAR_MESSAGE",
            data: {
              message: "app.case.protocol.changeCompletedDateSuccess",
              isSuccess: true,
            },
          });

          window.location.reload();
        } else {
          dispatch({ type: CHANGE_SURVEY_COMPLETED_DATE_FAILED, data: {} });
          dispatch({
            type: "SET_SNACKBAR_MESSAGE",
            data: {
              message: "app.case.protocol.changeCompletedDateFailed",
              isSuccess: false,
            },
          });
        }
      });
  };

const SET_ARE_ANY_ANSWERED_QUESTION_REQUESTED =
  "SET_ARE_ANY_ANSWERED_QUESTION_REQUESTED";
const SET_ARE_ANY_ANSWERED_QUESTION_SUCCESS =
  "SET_ARE_ANY_ANSWERED_QUESTION_SUCCESS";
const SET_ARE_ANY_ANSWERED_QUESTION_FAILED =
  "SET_ARE_ANY_ANSWERED_QUESTION_FAILED";

export const setAnyAnsweredQuestionState = (state) => (dispatch) => {
  return dispatch({
    type: SET_ARE_ANY_ANSWERED_QUESTION_SUCCESS,
    data: state,
  });
};

const FETCH_PROTOCOL_VERSIONS_FOR_CIRCLE_REQUESTED =
  "FETCH_PROTOCOL_VERSIONS_FOR_CIRCLE_REQUESTED";
const FETCH_PROTOCOL_VERSIONS_FOR_CIRCLE_SUCCESS =
  "FETCH_PROTOCOL_VERSIONS_FOR_CIRCLE_SUCCESS";
const FETCH_PROTOCOL_VERSIONS_FOR_CIRCLE_FAILED =
  "FETCH_PROTOCOL_VERSIONS_FOR_CIRCLE_FAILED";

export const fetchProtocolVersionsForCircle = (id) => (dispatch) => {
  dispatch({ type: FETCH_PROTOCOL_VERSIONS_FOR_CIRCLE_REQUESTED, data: {} });

  protocol.fetchVersionsForCircle(id).then((protocols) => {
    if (protocols) {
      return dispatch({
        type: FETCH_PROTOCOL_VERSIONS_FOR_CIRCLE_SUCCESS,
        data: protocols.data,
      });
    } else {
      return dispatch({
        type: FETCH_PROTOCOL_VERSIONS_FOR_CIRCLE_FAILED,
        data: {},
      });
    }
  });
  return;
};

const CLEAR_PROTOCOL_VERSIONS_REQUESTED = "CLEAR_PROTOCOL_VERSIONS_REQUESTED";

export const clearProtocolVersions = () => (dispatch) => {
  return dispatch({ type: CLEAR_PROTOCOL_VERSIONS_REQUESTED, data: {} });
};

const FETCH_CAN_RESEND_SURVEY_REMINDER_REQUESTED =
  "FETCH_CAN_RESEND_SURVEY_REMINDER_REQUESTED";
const FETCH_CAN_RESEND_SURVEY_REMINDER_SUCCESS =
  "FETCH_CAN_RESEND_SURVEY_REMINDER_SUCCESS";
const FETCH_CAN_RESEND_SURVEY_REMINDER_FAILED =
  "FETCH_CAN_RESEND_SURVEY_REMINDER_FAILED";

export const fetchCanResendSurveyReminders = (caseId) => (dispatch) => {
  dispatch({ type: FETCH_CAN_RESEND_SURVEY_REMINDER_REQUESTED, data: {} });

  survey.checkCanResendSurveyReminders(caseId).then((result) => {
    if (result) {
      return dispatch({
        type: FETCH_CAN_RESEND_SURVEY_REMINDER_SUCCESS,
        data: result.data,
      });
    } else {
      return dispatch({
        type: FETCH_CAN_RESEND_SURVEY_REMINDER_FAILED,
        data: {},
      });
    }
  });
  return;
};

const RESEND_SURVEY_REMINDER_REQUESTED = "RESEND_SURVEY_REMINDER_REQUESTED";
const RESEND_SURVEY_REMINDER_SUCCESS = "RESEND_SURVEY_REMINDER_SUCCESS";
const RESEND_SURVEY_REMINDER_FAILED = "RESEND_SURVEY_REMINDER_FAILED";

export const resendSurveyReminder = (caseId, instanceId) => (dispatch) => {
  dispatch(startSaving());
  dispatch({ type: RESEND_SURVEY_REMINDER_REQUESTED, data: {} });

  survey.resendSurveyReminder(caseId, instanceId).then((result) => {
    dispatch(stopSaving());
    dispatch(hideModal());
    if (result && result.isSuccessful) {
      dispatch({ type: RESEND_SURVEY_REMINDER_SUCCESS, data: result.data });
      return dispatch({
        type: "SET_SNACKBAR_MESSAGE",
        data: { message: "app.survey.resendReminderSuccess", isSuccess: true },
      });
    } else {
      dispatch({ type: RESEND_SURVEY_REMINDER_FAILED, data: {} });
      if (result && result.errorCode === 38) {
        return dispatch({
          type: "SET_SNACKBAR_MESSAGE",
          data: {
            message: "app.survey.resendReminderLimitExceededError",
            isSuccess: false,
          },
        });
      }
      return dispatch({
        type: "SET_SNACKBAR_MESSAGE",
        data: { message: "app.survey.resendReminderError", isSuccess: false },
      });
    }
  });
  return;
};

const FETCH_BILATERAL_AREAS_REQUESTED = "FETCH_BILATERAL_AREAS_REQUESTED";
const FETCH_BILATERAL_AREAS_SUCCESS = "FETCH_BILATERAL_AREAS_SUCCESS";
const FETCH_BILATERAL_AREAS_FAILED = "FETCH_BILATERAL_AREAS_FAILED";

export const fetchBilateralAreas = (versionId) => (dispatch) => {
  dispatch({ type: FETCH_BILATERAL_AREAS_REQUESTED, data: {} });

  protocol.fetchBilateralAreas(versionId).then((data) => {
    if (data && data.isSuccessful) {
      return dispatch({ type: FETCH_BILATERAL_AREAS_SUCCESS, data: data });
    } else {
      return dispatch({ type: FETCH_BILATERAL_AREAS_FAILED, data: {} });
    }
  });
  return;
};

const initial = {
  observationalProtocol: null,
  observationalProtocolPreTreatmentSurveys: [],
  observationalProtocolTreatmentSurveys: [],
  observationalProtocolPostTreatmentSurveys: [],
  observationalProtocols: [],
  survey: null,
  selectedSurveyDelegateId: null,
  selectedSurveyTask36Id: null,
  originalSurvey: null,
  surveyQuestions: null,
  surveyQuestionIds: null,
  surveyQuestionHash: null,
  originalSurveyQuestions: null,
  originalSurveyQuestionIds: null,
  originalSurveyQuestionHash: null,
  questions: [],
  revisions: [],
  isCreating: false,
  surveyRefreshed: false,
  surveyQuestionsLoading: false,
  isCreatingSurvey: false,
  sortField: "name",
  isDescending: true,
  filter: 0,
  hasProtocols: false,
  units: [
    { id: 0, label: "Inches" },
    { id: 1, label: "Centimeters" },
  ],
  autoCompleteSuggestions: [],
  yAxisAutoCompleteSuggestions: [],
  autoCompleteChildSuggestions: [],
  status: 0,
  areAnyAnsweredQuestion: false,
  protocolVersions: [],
  canResendSurveyReminders: [],
  bilateralAreas: null,
  patientCaseSurveyInstances: null,
  propagationModal: {
    isOpen: false,
    preview: null,
    correctionRequests: null,
    submitData: null,
  },
  editReasonModal: {
    isOpen: false,
    pendingSubmit: null,
  },
};

export const reducer = (state = initial, action) => {
  switch (action.type) {
    case FETCH_CHILD_PROTOCOL_AUTOCOMPLETE_SUCCESS:
      return { ...state, autoCompleteChildSuggestions: action.data };
    case FETCH_PROTOCOL_AUTOCOMPLETE_SUCCESS:
      return { ...state, autoCompleteSuggestions: action.data };
    case FETCH_OBSERVATIONAL_PROTOCOL_REQUESTED:
      return {
        ...state,
        observationalProtocol: null,
        observationalProtocolPreTreatmentSurveys: [],
        observationalProtocolTreatmentSurveys: [],
        observationalProtocolPostTreatmentSurveys: [],
        revisions: [],
      };
      break;
    case FETCH_OBSERVATIONAL_PROTOCOL_SUCCESS:
      return {
        ...state,
        observationalProtocol: action.data,
        observationalProtocolPreTreatmentSurveys:
          action.data && action.data.preTreatmentSurveys,
        observationalProtocolTreatmentSurveys:
          action.data && action.data.treatmentSurveys,
        observationalProtocolPostTreatmentSurveys:
          action.data && action.data.postTreatmentSurveys,
      };
    case FETCH_OBSERVATIONAL_PROTOCOLS_SUCCESS:
      return {
        ...state,
        observationalProtocols: action.data,
        hasProtocols: action.data.length > 0,
      };
    case FETCH_SURVEY_SUCCESS:
      return {
        ...state,
        survey: action.data.survey,
        originalSurvey: action.data.survey,
        surveyQuestions: action.data.list,
        surveyQuestionHash: action.data.nodes,
        surveyQuestionIds: action.data.flat,
        originalSurveyQuestions: action.data.list,
        originalSurveyQuestionHash: action.data.nodes,
        originalSurveyQuestionIds: action.data.flat,
        patientCaseSurveyInstances: action.data.patientCaseSurveyInstances,
        surveyRefreshed: true,
        surveyQuestionsLoading: false,
      };
    case FETCH_SURVEY_QUESTIONS_REQUESTED:
      return {
        ...state,
        survey: null,
        originalSurvey: null,
        surveyQuestions: null,
        surveyQuestionHash: null,
        surveyQuestionIds: null,
        originalSurveyQuestions: null,
        originalSurveyQuestionHash: null,
        originalSurveyQuestionIds: null,
        patientCaseSurveyInstances: null,
        surveyRefreshed: true,
        surveyQuestionsLoading: true,
      };
    case FETCH_SURVEY_VALUES_REQUESTED:
      return {
        ...state,
        surveyId: null,
        survey: null,
        originalSurvey: null,
        surveyQuestions: null,
        surveyQuestionHash: null,
        surveyQuestionIds: null,
        originalSurveyQuestions: null,
        originalSurveyQuestionHash: null,
        originalSurveyQuestionIds: [],
        patientCaseSurveyInstances: null,
        surveyRefreshed: true,
      };
    case FETCH_SURVEY_VALUES_SUCCESS:
      return {
        ...state,
        status: action.data.status,
        surveyId: action.data.surveyId,
        survey: action.data.survey,
        originalSurvey: action.data.survey,
        surveyQuestions: action.data.list,
        surveyQuestionHash: action.data.nodes,
        surveyQuestionIds: action.data.flat,
        originalSurveyQuestions: action.data.list,
        originalSurveyQuestionHash: action.data.nodes,
        originalSurveyQuestionIds: action.data.flat,
        patientCaseSurveyInstances: action.data.patientCaseSurveyInstances,
        surveyRefreshed: true,
        revisions: action.data.revisions,
      };
    case FETCH_SURVEY_QUESTIONS_SUCCESS:
      return {
        ...state,
        questions: action.data,
      };
    case SUBMIT_SURVEY_ANSWER_SUCCESS:
      return {
        ...state,
        status: action.data.status,
        isCreatingSurvey: action.data.isCreatingSurvey,
      };
    case SUBMIT_SURVEY_ANSWER_FAILED:
      return {
        ...state,
        isCreatingSurvey: action.data.isCreatingSurvey,
      };
    case SUBMIT_SURVEY_ANSWER_REQUESTED:
      return {
        ...state,
        isCreatingSurvey: action.data.isCreatingSurvey,
      };
    case RESET_SURVEY:
      return {
        ...state,
        survey: null,
        originalSurvey: null,
        surveyQuestions: null,
        surveyQuestionHash: null,
        surveyQuestionIds: null,
        originalSurveyQuestions: null,
        originalSurveyQuestionHash: null,
        originalSurveyQuestionIds: null,
        patientCaseSurveyInstances: null,
        surveyRefreshed: false,
      };
    case SET_ARE_ANY_ANSWERED_QUESTION_SUCCESS:
      return {
        ...state,
        areAnyAnsweredQuestion: action.data,
      };
    case FETCH_PROTOCOL_VERSIONS_FOR_CIRCLE_FAILED:
    case FETCH_PROTOCOL_VERSIONS_FOR_CIRCLE_REQUESTED:
    case CLEAR_PROTOCOL_VERSIONS_REQUESTED:
      return {
        ...state,
        protocolVersions: [],
      };
    case FETCH_PROTOCOL_VERSIONS_FOR_CIRCLE_SUCCESS:
      return {
        ...state,
        protocolVersions: action.data,
      };
    case FETCH_CAN_RESEND_SURVEY_REMINDER_REQUESTED:
      return {
        ...state,
        canResendSurveyReminders: [],
      };
    case FETCH_CAN_RESEND_SURVEY_REMINDER_SUCCESS:
      return {
        ...state,
        canResendSurveyReminders: action.data,
      };

    case FETCH_BILATERAL_AREAS_FAILED:
    case FETCH_BILATERAL_AREAS_REQUESTED:
      return {
        ...state,
        bilateralAreas: null,
      };
    case FETCH_BILATERAL_AREAS_SUCCESS:
      return {
        ...state,
        bilateralAreas: action.data,
      };

    case "SIGN_OUT_REQUESTED":
      return initial;

    case "SHOW_PROPAGATION_MODAL":
      return {
        ...state,
        propagationModal: {
          isOpen: true,
          preview: action.data.preview,
          correctionRequests: action.data.correctionRequests,
          submitData: action.data.submitData,
        },
      };

    case "HIDE_PROPAGATION_MODAL":
      return {
        ...state,
        propagationModal: {
          isOpen: false,
          preview: null,
          correctionRequests: null,
          submitData: null,
        },
      };

    case "SHOW_EDIT_REASON_MODAL":
      return {
        ...state,
        editReasonModal: {
          isOpen: true,
          pendingSubmit: action.data.pendingSubmit,
        },
      };

    case "HIDE_EDIT_REASON_MODAL":
      return {
        ...state,
        editReasonModal: {
          isOpen: false,
          pendingSubmit: null,
        },
      };

    default:
      return { ...state };
  }
};

const mainSelector = (state) => state.protocol;
const filterSelector = (state) => state.protocol.filter;
export const unitListSelector = (state) => state.protocol.units;

export const surveyQuestionIdSelector = (state) =>
  state.protocol.surveyQuestionIds;
export const surveyRefreshedSelector = (state) =>
  state.protocol.surveyRefreshed;
export const surveyQuestionsLoadingSelector = (state) =>
  state.protocol.surveyQuestionsLoading;

export const hasProtocolSelector = createSelector(mainSelector, (state) => {
  return state.hasProtocols;
});
export const sortProtocolsDirectionSelector = createSelector(
  mainSelector,
  (state) => {
    return state.isDescending;
  },
);

export const sortProtocolsFieldSelector = createSelector(
  mainSelector,
  (state) => {
    return state.sortField;
  },
);

export const isCreatingProtocolSelector = createSelector(
  mainSelector,
  (state) => state.isCreating,
);

export const isCreatingSurveySelector = createSelector(
  mainSelector,
  (state) => state.isCreatingSurvey,
);

export const protocolSelector = createSelector(
  mainSelector,
  (state) => state.observationalProtocol,
);
export const preTreatmentSurveySelector = createSelector(
  mainSelector,
  (state) => state.observationalProtocolPreTreatmentSurveys,
);
export const treatmentSurveySelector = createSelector(
  mainSelector,
  (state) => state.observationalProtocolTreatmentSurveys,
);
export const postTreatmentSurveySelector = createSelector(
  mainSelector,
  (state) => state.observationalProtocolPostTreatmentSurveys,
);

export const protocolsSelector = createSelector(
  mainSelector,
  filterSelector,
  (state, filter) => {
    switch (filter) {
      case 0:
        return state.observationalProtocols;
      case 1:
        return state.observationalProtocols.filter((r) => {
          return r.isActive === true;
        });
      case 2:
        return state.observationalProtocols.filter((r) => {
          return r.isActive === false;
        });
    }
  },
);

export const formattedProtocolsSelector = createSelector(
  mainSelector,
  (state) => {
    if (state && state.observationalProtocols) {
      return state.observationalProtocols.map((r) => {
        return {
          id: r.id,
          name: r.name,
          label: r.name,
          isActive: r.isActive,
        };
      });
    }
    return [];
  },
);

export const childProtocolListSelector = createSelector(
  mainSelector,
  (state) => {
    if (state.observationalProtocol && state.observationalProtocol.children) {
      return state.observationalProtocol.children.map((r) => {
        return {
          label: r.name,
          id: r.id,
        };
      });
    }
    return null;
  },
);

export const surveySelector = createSelector(mainSelector, (state) => {
  return state.survey;
});

export const surveyFormSelector = createSelector(mainSelector, (state) => {
  if (state && state.surveyId) {
    return "form" + state.surveyId;
  }
});

export const surveyQuestionsSelector = createSelector(mainSelector, (state) => {
  return state.surveyQuestions && state.surveyQuestions.getList();
});

export const areAnyAnsweredQuestionSelector = createSelector(
  mainSelector,
  (state) => {
    return state.areAnyAnsweredQuestion;
  },
);

// export const surveyDelegateSelector = createSelector(mainSelector, (state) => {
// 	return
// })

export const flatSurveyQuestionsSelector = createSelector(
  mainSelector,
  surveyQuestionIdSelector,
  (state, ids) => {
    if (state && ids && ids.length > 0) {
      let questions = [];

      ids[0].map((r) => {
        if (state.surveyQuestionHash[0].hasOwnProperty(r)) {
          let x = state.surveyQuestionHash[r];

          questions.push(x);
        } else {
          console.info("No Property", questions);
        }
      });

      return questions;
    }
  },
);

export const protocolAutoCompleteSuggestionSelector = createSelector(
  mainSelector,
  (state) => {
    if (state && state.autoCompleteSuggestions) {
      return state.autoCompleteSuggestions;
    }
    return [];
  },
);
export const protocolAutoCompleteChildSuggestionSelector = createSelector(
  mainSelector,
  (state) => {
    if (state && state.autoCompleteChildSuggestions) {
      return state.autoCompleteChildSuggestions;
    }
    return [];
  },
);

// export const questionsSelector = createSelector(mainSelector, (state) => {
// 	return (
// 		state.questions &&
// 		state.questions.questions &&
// 		state.questions.questions.length > 0 &&
// 		state.questions.questions.sort((a, b) => (a.sortOrder > b.sortOrder ? 1 : -1))
// 	)
// })

export const yAxisAutoCompleteSuggestionSelector = createSelector(
  mainSelector,
  (state) => {
    if (state && state.yAxisAutoCompleteSuggestions) {
      return state.yAxisAutoCompleteSuggestions;
    }
    return [];
  },
);

export const questionListSelector = createSelector(mainSelector, (state) => {
  if (state && state.surveyQuestions) return state.surveyQuestions;
});

export const questionHashSelector = createSelector(mainSelector, (state) => {
  if (state && state.surveyQuestionHash) return state.surveyQuestionHash;
});

export const questionIdSelector = createSelector(mainSelector, (state) => {
  if (state && state.surveyQuestionIds) return state.surveyQuestionIds;
});

export const questionsSelector = createSelector(
  mainSelector,
  questionIdSelector,
  questionHashSelector,
  (state, idsContainers, hash) => {
    if (state && idsContainers && idsContainers.length > 0 && hash) {
      let questionContainers = [];

      idsContainers.map((ids, index) => {
        let questions = [];

        ids.map((r) => {
          if (hash[index].hasOwnProperty(r)) {
            let x = hash[index][r];

            // 🔍 ADD THIS LOGGING
            // console.log("Question Debug:", {
            //   id: x.data?.id,
            //   uniqueId: x.data?.uniqueId,
            //   title: x.data?.title,
            //   questionType: x.data?.questionType, // ← ADD THIS
            //   isRepeatable: x.data?.isRepeatable,
            //   isLocked: x.data?.isLocked,
            //   preFilledValue: x.data?.preFilledValue,
            //   value: x.data?.value, // ← ADD THIS - The actual parsed value
            //   url: x.data?.url, // ← ADD THIS - The actual parsed URL
            //   repeatableMetadata: x.data?.repeatableMetadata,
            // });

            questions.push(x);
          } else {
            console.info("No Property", questions);
          }
        });

        questionContainers.push(questions);
      });

      return questionContainers;
    }
  },
);

export const surveyAnswerRevisionsSelector = createSelector(
  mainSelector,
  (state) => {
    if (state && state.revisions) return state.revisions;
  },
);

export const initialValuesSelector = createSelector(
  mainSelector,
  questionIdSelector,
  questionHashSelector,
  (state, ids, hash) => {
    var initialObj = {};

    initialObj["answers"] = [2];
    return initialObj;
  },
);

export const protocolVersionsSelector = createSelector(
  mainSelector,
  (state) => {
    return state && state.protocolVersions;
  },
);

export const defaultProtocolVersionSelector = createSelector(
  mainSelector,
  (state) => {
    if (state && state.protocolVersions && state.protocolVersions.length > 0)
      return state.protocolVersions.find((x) => x.isDefault);
  },
);

export const canResendSurveyRemindersSelector = createSelector(
  mainSelector,
  (state) => {
    return state && state.canResendSurveyReminders;
  },
);

export const bilateralAreasSelector = createSelector(mainSelector, (state) => {
  return state && state.bilateralAreas;
});

export const propagationModalSelector = createSelector(
  mainSelector,
  (state) => state.propagationModal,
);

export const editReasonModalSelector = createSelector(
  mainSelector,
  (state) => state.editReasonModal,
);
